/* eslint-disable max-lines */
import {useEffect, useRef, useState} from 'react';

import {getCreditCardErrorText, PAYMENT_OS_ERRORS} from '@optimaxdev/utils';
import {useQuery} from '@tanstack/react-query';

import {ENDPOINTS} from 'constants/endpoints';
import {apiConfig} from 'libs/api/api';
import {createTokenFromPaymentOS} from 'libs/utils/paymentOS/paymentOS';

import {PAYMENTS} from '../../constants/paymentStep';

type ResponsePaymentOsSettingsType = {
    env: string;
    'public-key': string;
};

type ErrorType = {
    pan: string;
    expiry: string;
    cvv: string;
};

type FieldOptionsType = {
    error: ErrorType;
    complete: boolean;
    empty: boolean;
};

type FieldNameType = 'pan' | 'expiry' | 'cvv';

type FieldType = {
    [property in FieldNameType]: {
        id: string;
        type: string;
        classes: {
            base: string;
        };
        includeCardImage?: boolean;
    };
};

type PlaceholdersType = Partial<{cardNumber: string; expDate: string; cvv: string}>;

type OptionsType = {
    onSuccess: (data: {token: string; cvv: string}) => void;
    onReject: (error: string) => void;
};

const defaultPlaceholders: PlaceholdersType = {
    cardNumber: '* Card Number',
    cvv: '* CVV',
    expDate: '* MM / YY',
};

type ErrorsStateType = {
    pan: {
        message: string;
        complete: boolean;
    };
    expiry: {
        message: string;
        complete: boolean;
    };
    cvv: {
        message: string;
        complete: boolean;
    };
};

/**
 * PaymentsOS hooks, which provide payment os fields
 *
 * @param {FieldType} fields - properties
 * @param {PropsType} placeholders - properties
 * @returns {JSX.Element} component
 */
export const usePaymentOs = (fields: FieldType, placeholders = defaultPlaceholders) => {
    const [errors, setErrors] = useState<ErrorsStateType>({
        cvv: {
            complete: false,
            message: '',
        },
        expiry: {
            complete: false,
            message: '',
        },
        pan: {
            complete: false,
            message: '',
        },
    });

    const isFormInitialized = useRef(false);
    const [settings, saveSettings] = useState({
        cardNumberField: null,
        environment: '',
    });

    const {data, isSuccess} = useQuery({
        queryFn: () => {
            return apiConfig.instanceGusaBackend.request<ResponsePaymentOsSettingsType>({
                baseURL: apiConfig.API_URL,
                method: 'GET',
                url: ENDPOINTS.PAYMENTS_OS_SETTINGS,
            });
        },
        queryKey: [PAYMENTS.METHOD_CREDIT_CARD],
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        retry: false,
        retryOnMount: true,
    });

    useEffect(() => {
        if (data && isSuccess && !isFormInitialized.current) {
            const {
                data: {env, 'public-key': publicKey},
            } = data;
            const formElements = new window.POS.Fields(publicKey);
            isFormInitialized.current = true;

            Object.entries(fields).forEach(([field, {classes, id, includeCardImage, type}]) => {
                const createdField = formElements.create(type, {
                    classes,
                    includeCardImage,
                    placeholders,
                });

                createdField.mount(id);

                createdField.on('change', ({complete, empty}: FieldOptionsType) => {
                    if (!empty)
                        setErrors(prevState => ({
                            ...prevState,
                            [field]: {
                                complete,
                                message: '',
                            },
                        }));
                });

                createdField.on('blur', ({complete, empty, error}: FieldOptionsType) => {
                    if (error)
                        setErrors(prevState => ({
                            ...prevState,
                            [field]: {
                                complete,
                                message: getCreditCardErrorText(error[field as FieldNameType]),
                            },
                        }));

                    if (field === 'cvv' && (!complete || empty))
                        setErrors(prevState => ({
                            ...prevState,
                            [field]: {
                                complete,
                                message: 'Please enter a valid security code (CVV/CID)',
                            },
                        }));
                });

                if (field === 'pan') {
                    saveSettings({
                        cardNumberField: createdField,
                        environment: env,
                    });
                }
            });
        }
    }, [data, fields, isSuccess, placeholders]);

    /**
     * Set current error to errors, and return error message
     *
     * @param {FieldNameType} field - name of field
     * @param {string} errorMessage - error message
     * @param {OptionsType.onReject} onReject - onReject calls with error message
     *
     */
    const setNotCompleteFieldError = (field: FieldNameType, errorMessage: string) => {
        setErrors(prevState => ({
            ...prevState,
            [field]: {
                complete: errors[field].complete,
                message: errorMessage,
            },
        }));
    };

    // eslint-disable-next-line max-statements
    const createToken = (
        additionalData: {
            holder_name: string;
            additional_details: {
                tmp: string;
            };
        },
        {onReject, onSuccess}: OptionsType,
    ) => {
        if (!errors.pan.complete) {
            setNotCompleteFieldError('pan', PAYMENT_OS_ERRORS.PAN);
            onReject(PAYMENT_OS_ERRORS.PAN);
            return;
        }

        if (!errors.expiry.complete) {
            setNotCompleteFieldError('expiry', PAYMENT_OS_ERRORS.EXPIRY);
            onReject(PAYMENT_OS_ERRORS.EXPIRY);
            return;
        }

        if (!errors.cvv.complete) {
            setNotCompleteFieldError('cvv', PAYMENT_OS_ERRORS.CVV);
            onReject(PAYMENT_OS_ERRORS.CVV);
            return;
        }

        createTokenFromPaymentOS({
            additionalData,
            onReject: ({errorMessage, fieldName}) => {
                setErrors(prevState => ({
                    ...prevState,
                    [fieldName]: {
                        complete: false,
                        message: errorMessage,
                    },
                }));

                onReject(errorMessage);
            },
            onSuccess,
            ...settings,
        });
    };

    return {
        createToken,
        errors: errors.pan.message || errors.expiry.message || errors.cvv.message,
    };
};
